<template>
  <b-modal
    v-model="localVisible"
    size="lg"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    :return-focus="returnFocus || undefined"
    body-class="p-0"
    @hidden="onHidden"
  >
    <!-- Header customizado -->
    <template #modal-header>
      <div class="interr-header">
        <div class="d-flex align-items-center justify-content-between w-100">
          <div class="interr-title">
            <feather-icon
              icon="ClipboardIcon"
              size="17"
              class="mr-75 text-primary"
            />
            {{ interrogatory ? interrogatory.name : "Interrogatório" }}
          </div>
          <div class="d-flex align-items-center" style="gap: 8px">
            <span
              v-if="mandatory && !finished && mode === 'flow'"
              class="step-pill step-pill--mandatory"
            >
              <feather-icon icon="LockIcon" size="11" class="mr-25" />
              Obrigatório
            </span>
            <span
              v-if="mode === 'flow' && !finished && currentQuestion"
              class="step-pill"
            >
              Pergunta {{ history.length + 1 }} de
              {{ interrogatory ? interrogatory.questions.length : "?" }}
            </span>
            <span
              v-else-if="mode === 'review'"
              class="step-pill step-pill--review"
            >
              <feather-icon icon="EyeIcon" size="11" class="mr-25" /> Revisão
            </span>
            <span v-else-if="finished" class="step-pill step-pill--done">
              <feather-icon icon="CheckIcon" size="11" class="mr-25" />
              Concluído
            </span>
            <button
              v-if="!mandatory || finished || mode === 'review'"
              type="button"
              class="interr-x"
              @click="localVisible = false"
            >
              <feather-icon icon="XIcon" size="17" />
            </button>
          </div>
        </div>
        <div v-if="mode === 'flow'" class="interr-progress-track mt-2">
          <div
            class="interr-progress-fill"
            :style="{ width: progressValue + '%' }"
          />
        </div>
      </div>
    </template>
    <!-- Sem perguntas -->
    <div v-if="!interrogatory || !rootQuestion" class="text-center py-5">
      <feather-icon icon="InboxIcon" size="36" class="text-muted mb-2" />
      <p class="text-muted mb-3">Nenhuma pergunta disponível.</p>
      <b-button v-if="!mandatory" variant="outline-secondary" @click="skip"
        >Pular</b-button
      >
    </div>

    <!-- Modo revisão (somente leitura) -->
    <template v-else-if="mode === 'review'">
      <div class="review-panel">
        <p class="text-muted text-center mb-3" style="font-size: 0.85rem">
          Resumo das respostas fornecidas para este interrogatório.
        </p>
        <div v-if="reviewList.length === 0" class="text-center text-muted py-3">
          Nenhuma resposta registrada.
        </div>
        <div v-for="(ans, idx) in reviewList" :key="idx" class="review-item">
          <div class="review-num">{{ idx + 1 }}</div>
          <div class="review-content">
            <div class="review-q">{{ ans.questionText }}</div>
            <span class="review-ans" :class="reviewAnswerClass(ans)">
              <feather-icon
                :icon="reviewAnswerIcon(ans)"
                size="12"
                class="mr-25"
              />
              {{ reviewAnswerText(ans) }}
            </span>
          </div>
        </div>
      </div>
      <div class="interr-footer">
        <div />
        <b-button variant="primary" @click="localVisible = false">
          <feather-icon icon="XIcon" size="14" class="mr-50" /> Fechar
        </b-button>
      </div>
    </template>

    <!-- Modo flow -->
    <template v-else>
      <!-- Pergunta atual -->
      <div v-if="!finished" class="question-area">
        <div class="question-bubble">
          <p class="question-text">{{ currentQuestion.question }}</p>
          <span v-if="currentQuestion.required" class="required-tag">
            <feather-icon
              icon="AlertCircleIcon"
              size="12"
              class="mr-25"
            />Obrigatória
          </span>
        </div>

        <!-- Tipo 1: Sim / Não -->
        <template
          v-if="
            currentQuestion.question_type === 1 ||
            !currentQuestion.question_type
          "
        >
          <div class="bool-grid">
            <div
              class="bool-card bool-yes"
              :class="{ 'bool-active': currentBoolAnswer === true }"
              @click="answerBoolean(true)"
            >
              <div class="bool-icon-wrap">
                <feather-icon icon="CheckIcon" size="36" />
              </div>
              <div class="bool-label">Sim</div>
            </div>
            <div
              class="bool-card bool-no"
              :class="{ 'bool-active': currentBoolAnswer === false }"
              @click="answerBoolean(false)"
            >
              <div class="bool-icon-wrap">
                <feather-icon icon="XIcon" size="36" />
              </div>
              <div class="bool-label">Não</div>
            </div>
          </div>
        </template>

        <!-- Tipo 2: Texto livre -->
        <template v-else-if="currentQuestion.question_type === 2">
          <textarea
            class="form-control mt-3"
            rows="4"
            v-model="currentTextValue"
            placeholder="Digite sua resposta aqui..."
          />
        </template>

        <!-- Tipo 3: Número -->
        <template v-else-if="currentQuestion.question_type === 3">
          <div class="num-wrap mt-3">
            <input
              type="number"
              class="form-control form-control-lg text-center"
              v-model.number="currentNumberValue"
              placeholder="0"
              step="any"
            />
          </div>
        </template>

        <!-- Tipo 4: Alternativas -->
        <template v-else-if="currentQuestion.question_type === 4">
          <div class="alt-list mt-3">
            <div
              v-for="alt in currentQuestion.alternatives"
              :key="alt.id"
              class="alternative-option"
              :class="{ selected: currentAlternativeId === alt.id }"
              @click="currentAlternativeId = alt.id"
            >
              <span
                class="alt-radio"
                :class="{ active: currentAlternativeId === alt.id }"
              />
              <span>{{ alt.label }}</span>
            </div>
            <p
              v-if="
                !currentQuestion.alternatives ||
                !currentQuestion.alternatives.length
              "
              class="text-muted text-center mt-2"
            >
              Nenhuma alternativa cadastrada.
            </p>
          </div>
        </template>
      </div>

      <!-- Conclusão com mini-resumo -->
      <div v-else class="finished-area">
        <div class="text-center mb-3">
          <feather-icon icon="CheckCircleIcon" size="52" class="text-success" />
          <h5 class="mt-2 mb-1">Tudo respondido!</h5>
          <p class="text-muted" style="font-size: 0.9rem">
            Verifique as respostas abaixo antes de confirmar.
          </p>
        </div>
        <div class="review-panel">
          <div
            v-for="(ans, idx) in finishedReviewList"
            :key="idx"
            class="review-item"
          >
            <div class="review-num">{{ idx + 1 }}</div>
            <div class="review-content">
              <div class="review-q">{{ ans.questionText }}</div>
              <span class="review-ans" :class="reviewAnswerClass(ans)">
                <feather-icon
                  :icon="reviewAnswerIcon(ans)"
                  size="12"
                  class="mr-25"
                />
                {{ reviewAnswerText(ans) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Rodapé -->
      <div class="interr-footer">
        <div class="d-flex align-items-center" style="gap: 8px">
          <b-button
            v-if="history.length > 0 && !finished"
            variant="outline-secondary"
            size="sm"
            @click="goBack"
          >
            <feather-icon icon="ArrowLeftIcon" size="13" class="mr-25" /> Voltar
          </b-button>
          <b-button
            v-if="!mandatory && !finished"
            variant="flat-secondary"
            size="sm"
            class="text-muted"
            @click="skip"
          >
            Pular interrogatório
          </b-button>
          <span
            v-if="showNextButton && !currentAnswerReady"
            class="answer-hint answer-hint--pending"
          >
            <feather-icon icon="ArrowUpIcon" size="12" class="mr-25" />
            {{ pendingHint }}
          </span>
          <span
            v-else-if="showNextButton"
            class="answer-hint answer-hint--ready"
          >
            <feather-icon icon="CheckIcon" size="12" class="mr-25" />
            Resposta registrada
          </span>
          <span
            v-else-if="mandatory && !finished"
            class="answer-hint answer-hint--pending"
          >
            <feather-icon icon="LockIcon" size="12" class="mr-25" />
            Preenchimento obrigatório
          </span>
        </div>
        <div>
          <b-button
            v-if="showNextButton"
            variant="primary"
            class="interr-next"
            :class="{ 'interr-next--ready': currentAnswerReady }"
            :disabled="!currentAnswerReady"
            @click="advance"
          >
            Próximo
            <feather-icon icon="ArrowRightIcon" size="16" class="ml-50" />
          </b-button>
          <b-button
            v-if="finished"
            variant="success"
            :disabled="!canConfirm"
            @click="confirm"
          >
            <feather-icon icon="SaveIcon" size="14" class="mr-50" /> Confirmar
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BButton, BModal } from "bootstrap-vue";

export default {
  name: "InterrogatoryModal",

  components: { BButton, BModal },

  props: {
    interrogatory: { type: Object, default: null },
    visible: { type: Boolean, default: false },
    /** Seletor/elemento que recebe o foco ao fechar (padrão do BModal: quem tinha o foco antes). */
    returnFocus: { type: [String, Object], default: null },
    /** Passa as respostas já confirmadas para abrir em modo revisão */
    reviewAnswers: { type: Array, default: () => [] },
    /** Quando true, impede pular e fechar antes de responder */
    mandatory: { type: Boolean, default: false },
  },

  data() {
    return {
      localVisible: false,
      mode: "flow", // 'flow' | 'review'
      history: [],
      currentQuestion: null,
      answers: {},
      finished: false,
      currentTextValue: "",
      currentNumberValue: null,
      currentAlternativeId: null,
    };
  },

  computed: {
    rootQuestion() {
      if (!this.interrogatory?.questions) return null;
      return (
        this.interrogatory.questions.find((q) => q.is_root) ||
        this.interrogatory.questions[0] ||
        null
      );
    },

    currentBoolAnswer() {
      if (!this.currentQuestion) return undefined;
      const a = this.answers[this.currentQuestion.id];
      return a ? a.answer_boolean : undefined;
    },

    currentQuestionType() {
      return this.currentQuestion ? this.currentQuestion.question_type || 1 : 1;
    },

    showNextButton() {
      if (this.finished || !this.currentQuestion) return false;
      return [2, 3, 4].includes(this.currentQuestionType);
    },

    currentAnswerReady() {
      if (!this.currentQuestion) return false;
      const required = !!this.currentQuestion.required;

      if (this.currentQuestionType === 2) {
        return !required || !!(this.currentTextValue || "").trim();
      }
      if (this.currentQuestionType === 3) {
        return (
          !required
          || (this.currentNumberValue !== null && this.currentNumberValue !== "")
        );
      }
      if (this.currentQuestionType === 4) {
        return !!this.currentAlternativeId;
      }
      return true;
    },

    pendingHint() {
      if (this.currentQuestionType === 2) {
        return "Digite a resposta para continuar";
      }
      if (this.currentQuestionType === 3) {
        return "Informe um valor para continuar";
      }
      if (this.currentQuestionType === 4) {
        return "Selecione uma opção para continuar";
      }
      return "Escolha Sim ou Não para continuar";
    },

    answeredQuestions() {
      const seen = new Set();
      const list = [];

      this.history.forEach((q) => {
        if (seen.has(q.id)) return;
        if (this.answers[q.id] === undefined) return;
        seen.add(q.id);
        list.push({
          interrogatory_question_id: q.id,
          ...this.answers[q.id],
        });
      });

      return list;
    },

    canConfirm() {
      const visited = Object.keys(this.answers).map(Number);
      const requiredVisited = (this.interrogatory?.questions || []).filter(
        (q) => q.required && visited.includes(q.id),
      );
      return requiredVisited.every((q) => {
        const a = this.answers[q.id];
        if (!a) return false;
        if (a.answer_boolean !== undefined && a.answer_boolean !== null)
          return true;
        if (
          a.answer_text !== undefined &&
          a.answer_text !== null &&
          String(a.answer_text).trim() !== ""
        )
          return true;
        if (a.answer_number !== undefined && a.answer_number !== null)
          return true;
        if (
          a.answer_alternative_id !== undefined &&
          a.answer_alternative_id !== null
        )
          return true;
        return false;
      });
    },

    progressValue() {
      if (!this.interrogatory?.questions?.length) return 0;
      if (this.finished) return 100;
      return Math.round(
        (this.history.length / this.interrogatory.questions.length) * 100,
      );
    },

    reviewList() {
      return (this.reviewAnswers || []).map((ans) => {
        const q = (this.interrogatory?.questions || []).find(
          (q) => q.id === ans.interrogatory_question_id,
        );
        return {
          questionText: q
            ? q.question
            : `Pergunta ${ans.interrogatory_question_id}`,
          _q: q || null,
          ...ans,
        };
      });
    },

    finishedReviewList() {
      return this.answeredQuestions.map((ans) => {
        const q = (this.interrogatory?.questions || []).find(
          (qq) => qq.id === ans.interrogatory_question_id,
        );
        return {
          questionText: q
            ? q.question
            : `Pergunta ${ans.interrogatory_question_id}`,
          _q: q || null,
          ...ans,
        };
      });
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.localVisible = val;
        if (val) {
          if (this.reviewAnswers && this.reviewAnswers.length > 0) {
            this.mode = "review";
          } else {
            this.mode = "flow";
            this.init();
          }
        }
      },
    },
    localVisible(val) {
      if (!val) this.$emit("update:visible", false);
    },
    currentQuestion(q) {
      if (!q) return;
      const existing = this.answers[q.id];
      this.currentTextValue = existing?.answer_text ?? "";
      this.currentNumberValue = existing?.answer_number ?? null;
      this.currentAlternativeId = existing?.answer_alternative_id ?? null;
    },
  },

  methods: {
    init() {
      this.history = [];
      this.answers = {};
      this.finished = false;
      this.currentTextValue = "";
      this.currentNumberValue = null;
      this.currentAlternativeId = null;
      this.currentQuestion = this.rootQuestion;
    },

    answerBoolean(value) {
      const q = this.currentQuestion;
      this.$set(this.answers, q.id, { answer_boolean: value });
      const nextId = value
        ? q.next_question_id_if_yes
        : q.next_question_id_if_no;
      this.history.push(q);
      this._goTo(nextId);
    },

    advance() {
      if (!this.currentAnswerReady) return;
      if (this.currentQuestionType === 2) this.answerText();
      else if (this.currentQuestionType === 3) this.answerNumber();
      else if (this.currentQuestionType === 4) this.answerAlternative();
    },

    answerText() {
      if (!this.currentAnswerReady) return;
      const q = this.currentQuestion;
      this.$set(this.answers, q.id, { answer_text: this.currentTextValue });
      this.history.push(q);
      this._goTo(q.next_question_id);
    },

    answerNumber() {
      if (!this.currentAnswerReady) return;
      const q = this.currentQuestion;
      this.$set(this.answers, q.id, { answer_number: this.currentNumberValue });
      this.history.push(q);
      this._goTo(q.next_question_id);
    },

    answerAlternative() {
      const q = this.currentQuestion;
      const alt = (q.alternatives || []).find(
        (a) => a.id === this.currentAlternativeId,
      );
      if (!alt) return;
      this.$set(this.answers, q.id, { answer_alternative_id: alt.id });
      this.history.push(q);
      this._goTo(alt.next_question_id);
    },

    _goTo(nextId) {
      if (!nextId) {
        this.finished = true;
        this.currentQuestion = null;
        return;
      }
      const next = (this.interrogatory.questions || []).find(
        (q) => q.id === nextId,
      );
      if (!next) {
        this.finished = true;
        this.currentQuestion = null;
        return;
      }
      this.currentQuestion = next;
    },

    goBack() {
      if (!this.history.length) return;
      const prev = this.history.pop();
      this.finished = false;
      this.currentQuestion = prev;
    },

    confirm() {
      this.$emit("confirm", {
        interrogatory_id: this.interrogatory.id,
        answers: this.answeredQuestions,
      });
      this.localVisible = false;
    },

    skip() {
      this.$emit("skip");
      this.localVisible = false;
    },

    onHidden() {
      // Bloqueia fechamento acidental quando obrigatório e não respondido
      if (this.mandatory && !this.finished && this.mode === "flow") {
        this.$nextTick(() => {
          this.localVisible = true;
        });
        return;
      }
      this.$emit("close");
    },

    reviewAnswerText(ans) {
      if (ans.answer_boolean !== undefined && ans.answer_boolean !== null)
        return ans.answer_boolean ? "Sim" : "Não";
      if (ans.answer_text !== undefined && ans.answer_text !== null)
        return ans.answer_text;
      if (ans.answer_number !== undefined && ans.answer_number !== null)
        return String(ans.answer_number);
      if (
        ans.answer_alternative_id !== undefined &&
        ans.answer_alternative_id !== null
      ) {
        const alt = (ans._q?.alternatives || []).find(
          (a) => a.id === ans.answer_alternative_id,
        );
        return alt ? alt.label : String(ans.answer_alternative_id);
      }
      return "-";
    },

    reviewAnswerClass(ans) {
      if (ans.answer_boolean === true) return "ans-yes";
      if (ans.answer_boolean === false) return "ans-no";
      if (ans.answer_number !== undefined && ans.answer_number !== null)
        return "ans-number";
      if (
        ans.answer_alternative_id !== undefined &&
        ans.answer_alternative_id !== null
      )
        return "ans-alt";
      return "ans-text";
    },

    reviewAnswerIcon(ans) {
      if (ans.answer_boolean === true) return "CheckCircleIcon";
      if (ans.answer_boolean === false) return "XCircleIcon";
      if (ans.answer_number !== undefined && ans.answer_number !== null)
        return "HashIcon";
      if (
        ans.answer_alternative_id !== undefined &&
        ans.answer_alternative_id !== null
      )
        return "ListIcon";
      return "MessageSquareIcon";
    },
  },
};
</script>

<style scoped>
::v-deep .modal-header {
  padding: 0 !important;
  border-bottom: none !important;
}

.interr-header {
  padding: 1.1rem 1.4rem 0;
  width: 100%;
}

.interr-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}

.step-pill {
  font-size: 0.75rem;
  font-weight: 600;
  background: #f0eeff;
  color: #7367f0;
  padding: 4px 11px;
  border-radius: 20px;
  white-space: nowrap;
}
.step-pill--review {
  background: #fff5e6;
  color: #ff9f43;
}
.step-pill--done {
  background: #e7faf0;
  color: #28c76f;
}
.step-pill--mandatory {
  background: #ffeaea;
  color: #ea5455;
}

.interr-x {
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  color: #6e6b7b;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: background 0.15s;
}
.interr-x:hover {
  background: #f8f7ff;
  color: #7367f0;
}

.interr-progress-track {
  height: 4px;
  background: #ebe9f1;
  border-radius: 2px;
  overflow: hidden;
  margin-left: -1.4rem;
  margin-right: -1.4rem;
}
.interr-progress-fill {
  height: 100%;
  background: linear-gradient(to right, #7367f0, #9e95f5);
  border-radius: 2px;
  transition: width 0.4s ease;
}

.question-area {
  padding: 2rem 2.5rem 1rem;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.question-bubble {
  text-align: center;
  max-width: 540px;
  margin-bottom: 0.25rem;
}
.question-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #3d3b47;
  line-height: 1.65;
  margin-bottom: 0.4rem;
}
.required-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: #ea5455;
  background: #ffeaea;
  padding: 3px 9px;
  border-radius: 12px;
}

.bool-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  width: 100%;
  max-width: 380px;
  margin-top: 2rem;
}
.bool-card {
  border-radius: 14px;
  border: 2.5px solid #dee2e6;
  padding: 1.8rem 1rem 1.4rem;
  text-align: center;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.bool-card:hover {
  transform: translateY(-3px);
}
.bool-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}
.bool-label {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.bool-yes {
  border-color: #28c76f;
  color: #28c76f;
}
.bool-yes:hover {
  background: rgba(40, 199, 111, 0.07);
  box-shadow: 0 6px 18px rgba(40, 199, 111, 0.2);
}
.bool-yes.bool-active {
  background: #28c76f;
  color: white;
  box-shadow: 0 8px 22px rgba(40, 199, 111, 0.4);
}

.bool-no {
  border-color: #ea5455;
  color: #ea5455;
}
.bool-no:hover {
  background: rgba(234, 84, 85, 0.07);
  box-shadow: 0 6px 18px rgba(234, 84, 85, 0.2);
}
.bool-no.bool-active {
  background: #ea5455;
  color: white;
  box-shadow: 0 8px 22px rgba(234, 84, 85, 0.35);
}

.num-wrap {
  max-width: 200px;
  margin: 0 auto;
}

.alt-list {
  width: 100%;
  max-width: 500px;
}
.alternative-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1.5px solid #e0ddf5;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  font-size: 0.95rem;
  color: #5e5873;
}
.alternative-option:hover {
  border-color: #7367f0;
  background: #f8f7ff;
}
.alternative-option.selected {
  border-color: #7367f0;
  background: #eeecff;
  font-weight: 600;
}
.alt-radio {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #adb5bd;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;
}
.alt-radio.active {
  border-color: #7367f0;
  background: #7367f0;
}

.finished-area {
  padding: 1.5rem 2rem 0.5rem;
}

.review-panel {
  padding: 0.5rem 1.5rem 0.25rem;
}
.review-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f2f7;
}
.review-item:last-child {
  border-bottom: none;
}
.review-num {
  min-width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f0eeff;
  color: #7367f0;
  font-size: 0.72rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}
.review-content {
  flex: 1;
}
.review-q {
  font-size: 0.88rem;
  color: #5e5873;
  font-weight: 500;
  margin-bottom: 5px;
  line-height: 1.4;
}
.review-ans {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
}
.ans-yes {
  background: #e7faf0;
  color: #28c76f;
}
.ans-no {
  background: #ffeaea;
  color: #ea5455;
}
.ans-text {
  background: #f0eeff;
  color: #7367f0;
}
.ans-number {
  background: #fff5e6;
  color: #ff9f43;
}
.ans-alt {
  background: #e8f4fd;
  color: #00cfe8;
}

.interr-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #f3f2f7;
}

.answer-hint {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.answer-hint--pending {
  background: #fff5e6;
  color: #a15c00;
}
.answer-hint--ready {
  background: #e8f7ee;
  color: #15803d;
}

.interr-next {
  min-width: 152px;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: box-shadow 0.18s, opacity 0.18s;
}
.interr-next:disabled {
  opacity: 0.5;
  box-shadow: none;
}
.interr-next--ready {
  box-shadow: 0 4px 14px rgba(115, 103, 240, 0.45);
  animation: interr-next-pop 0.28s ease-out;
}
@keyframes interr-next-pop {
  0% {
    transform: scale(0.96);
  }
  60% {
    transform: scale(1.04);
  }
  100% {
    transform: scale(1);
  }
}

.dark-layout .answer-hint--pending {
  background: #3a3320;
  color: #fbbf24;
}
.dark-layout .answer-hint--ready {
  background: #14301f;
  color: #4ade80;
}
</style>
